import React, { useMemo, useState } from 'react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  buildSegments,
  getEventTypeMeta,
  getPositions,
  getReasonMeta,
} from './entityTagHistory.utils';
import {
  NOT_TAGGED_COLOR,
  TAGGED_COLOR,
  TOOLTIP_DEFAULT_GLYPH_COLOR,
  TOOLTIP_EXPLICIT_GLYPH_COLOR,
  UNKNOWN_PATTERN,
  legendClasses,
  segmentedControlClasses,
  summaryCardClasses,
  summaryClasses,
  timelineAxisClasses,
  timelineClasses,
  timelineTooltipClasses,
  toneColors,
} from './EntityTagHistory.styles';
import {
  ActorLabel,
  DirectionGlyph,
  TagStateLabel,
} from './EntityTagHistoryParts.component';
import type {
  ChildrenProps,
  EntityTagHistorySummaryProps,
  EntityTagHistoryTimelineProps,
  LegendProps,
  Scale,
  SegmentedControlProps,
  SummaryCardProps,
  TimelineAxisProps,
  TimelineTooltipProps,
} from './entityTagHistory.types';

export function EntityTagHistorySummary({
  summary,
  latestEvent,
  actorNames,
  isComplete,
}: EntityTagHistorySummaryProps) {
  const lastChange = summary.lastStateChange;

  return (
    <div className={summaryClasses.grid}>
      <SummaryCard label={isComplete ? 'Current state' : 'Latest loaded state'}>
        <div className={summaryClasses.stateLabel}>
          <TagStateLabel tagged={summary.latestState} />
        </div>
        <Caption>
          {latestEvent
            ? `Last event: ${getEventTypeMeta(latestEvent.eventType).label} · ${format(
                latestEvent.time,
                'MMM d, yyyy'
              )}`
            : 'No timestamped events'}
          {!isComplete && ' · more history available'}
        </Caption>
      </SummaryCard>

      <SummaryCard label="State changes">
        <BigNumber>{summary.stateChanges}</BigNumber>
        <Caption>
          {summary.totalEvents} event{summary.totalEvents === 1 ? '' : 's'} ·{' '}
          {summary.uniqueActors} actor{summary.uniqueActors === 1 ? '' : 's'}
        </Caption>
      </SummaryCard>

      <SummaryCard label="Last state change">
        <BigNumber>
          {lastChange
            ? formatDistanceToNowStrict(lastChange.time, { addSuffix: true })
            : '-'}
        </BigNumber>
        {lastChange && (
          <Caption>
            <span className={summaryClasses.lastChangeActor}>
              by <ActorLabel actorId={lastChange.actorId} actorNames={actorNames} />
            </span>
          </Caption>
        )}
      </SummaryCard>
    </div>
  );
}

export function EntityTagHistoryTimeline({
  events,
  actorNames,
  isComplete,
  now,
}: EntityTagHistoryTimelineProps) {
  const [scale, setScale] = useState<Scale>('time');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const segments = useMemo(
    () => buildSegments(events, now, isComplete),
    [events, now, isComplete]
  );
  const positions = getPositions(segments, scale);
  const markers = events.map((event, index) => ({ event, left: positions[index].left }));
  const isCrowded =
    scale === 'time' &&
    markers.some(
      (marker, index) => index > 0 && marker.left - markers[index - 1].left < 1
    );
  const timelineStart = segments[0]?.start;
  const timelineEnd = segments[segments.length - 1]?.end;
  const active = activeIndex === null ? undefined : markers[activeIndex];

  return (
    <div className={timelineClasses.root}>
      <div className={timelineClasses.controls}>
        <Legend isComplete={isComplete} />
        <SegmentedControl
          label="Timeline scale"
          value={scale}
          onChange={setScale}
          options={[
            { value: 'time', label: 'Actual time' },
            { value: 'even', label: 'Even spacing' },
          ]}
        />
      </div>

      <div className={timelineClasses.chart}>
        {active && (
          <TimelineTooltip
            event={active.event}
            left={active.left}
            actorNames={actorNames}
          />
        )}
        <div
          role="img"
          aria-label={`Tag state timeline with ${events.length} events`}
          className={timelineClasses.bar}
        >
          {segments.map((segment, index) => (
            <div
              key={`${segment.start}-${index}`}
              title={
                segment.tagged === undefined
                  ? 'History after this point has not been loaded'
                  : undefined
              }
              className={timelineClasses.segment(
                segment.tagged,
                activeIndex !== null && activeIndex !== index
              )}
              style={{
                left: `${positions[index].left}%`,
                width: `${positions[index].width}%`,
              }}
            />
          ))}
        </div>

        <div className={timelineClasses.markers}>
          {markers.map(({ event, left }, index) => {
            const meta = getEventTypeMeta(event.eventType);
            return (
              <button
                key={`${event.time}-${index}`}
                type="button"
                aria-label={`${meta.label} on ${format(event.time, 'PPpp')} by ${
                  (event.actorId && actorNames[event.actorId]?.name) ||
                  event.actorId ||
                  'unknown actor'
                }. ${event.tagExists ? 'Tagged' : 'Not tagged'} afterwards.`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
                onFocus={() => setActiveIndex(index)}
                onBlur={() => setActiveIndex(null)}
                className={timelineClasses.marker(
                  activeIndex === index,
                  meta.direction,
                  meta.tone
                )}
                style={{ left: `${left}%` }}
              />
            );
          })}
        </div>

        <TimelineAxis
          scale={scale}
          start={timelineStart}
          end={timelineEnd}
          isComplete={isComplete}
        />
      </div>

      {isCrowded && (
        <p className={timelineClasses.crowdedNotice}>
          Some events may have happened close together and overlap.{' '}
          <button
            type="button"
            onClick={() => setScale('even')}
            className={timelineClasses.crowdedNoticeButton}
          >
            Switch to even spacing
          </button>{' '}
          to inspect each one.
        </p>
      )}
    </div>
  );
}

function TimelineTooltip({ event, left, actorNames }: TimelineTooltipProps) {
  const meta = getEventTypeMeta(event.eventType);
  const translate = left < 18 ? '0%' : left > 82 ? '-100%' : '-50%';

  return (
    <div
      role="tooltip"
      className={timelineTooltipClasses.root(translate)}
      style={{ left: `calc(${left}% + 8px)` }}
    >
      <span className={timelineTooltipClasses.title}>
        <DirectionGlyph
          direction={meta.direction}
          color={
            meta.tone === 'explicit'
              ? TOOLTIP_EXPLICIT_GLYPH_COLOR
              : TOOLTIP_DEFAULT_GLYPH_COLOR
          }
          size={10}
        />
        {meta.label}
      </span>
      <span className={timelineTooltipClasses.description}>{meta.description}</span>
      <span className={timelineTooltipClasses.timestamp}>
        {format(event.time, 'PPpp')}
      </span>
      <span>
        By{' '}
        <strong>
          {(event.actorId && actorNames[event.actorId]?.name) ||
            event.actorId ||
            'Unknown'}
        </strong>
        {event.actorId && actorNames[event.actorId]?.username && (
          <div className={timelineTooltipClasses.username}>
            Username: {actorNames[event.actorId].username}
          </div>
        )}
      </span>
      <span>
        Afterwards:{' '}
        <strong className={timelineTooltipClasses.tagState(!!event.tagExists)}>
          {event.tagExists ? 'Tagged' : 'Not tagged'}
        </strong>{' '}
        · {getReasonMeta(event.tagExistsReason).label}
      </span>
      {event.materialChange && <span>Material change</span>}
    </div>
  );
}

function TimelineAxis({ scale, start, end, isComplete }: TimelineAxisProps) {
  if (start === undefined || end === undefined) return null;
  const endLabel = isComplete ? 'Now' : 'Now (not loaded)';
  const ticks =
    scale === 'time' && end > start
      ? [0, 0.25, 0.5, 0.75].map((fraction) => ({
          left: fraction * 100,
          label: format(start + (end - start) * fraction, 'MMM d'),
        }))
      : [{ left: 0, label: format(start, 'MMM d, yyyy') }];

  return (
    <div aria-hidden className={timelineAxisClasses.root}>
      {ticks.map((tick) => (
        <span
          key={tick.left}
          className={timelineAxisClasses.tick(tick.left === 0)}
          style={{ left: `${tick.left}%` }}
        >
          {tick.label}
        </span>
      ))}
      <span className={timelineAxisClasses.endLabel}>{endLabel}</span>
    </div>
  );
}

function Legend({ isComplete }: LegendProps) {
  const stateItems = [
    { label: 'Tagged', background: TAGGED_COLOR },
    { label: 'Not tagged', background: NOT_TAGGED_COLOR },
    ...(isComplete ? [] : [{ label: 'Not loaded yet', background: UNKNOWN_PATTERN }]),
  ];
  const markerItems = LEGEND_EVENT_TYPES.map((type) => getEventTypeMeta(type));

  return (
    <div className={legendClasses.root}>
      <div className={legendClasses.row}>
        <span className={legendClasses.groupLabel}>Bar color = tag state</span>
        {stateItems.map((item) => (
          <span key={item.label} className={legendClasses.item}>
            <span className={legendClasses.swatch(item.background)} />
            {item.label}
          </span>
        ))}
      </div>
      <div className={legendClasses.row}>
        <span className={legendClasses.groupLabel}>Marker = what happened</span>
        {markerItems.map((meta) => (
          <span key={meta.label} className={legendClasses.item} title={meta.description}>
            <DirectionGlyph
              direction={meta.direction}
              color={toneColors[meta.tone].solid}
              size={10}
            />
            {meta.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const LEGEND_EVENT_TYPES = [
  'ADDED',
  'REMOVED',
  'ADDED_PERCENT_BUCKET',
  'REMOVED_PERCENT_BUCKET',
] as const;

export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: SegmentedControlProps<T>) {
  return (
    <div role="group" aria-label={label} className={segmentedControlClasses.root}>
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            className={segmentedControlClasses.option(isSelected)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function SummaryCard({ label, children }: SummaryCardProps) {
  return (
    <section className={summaryCardClasses.root}>
      <h4 className={summaryCardClasses.label}>{label}</h4>
      {children}
    </section>
  );
}

const BigNumber = ({ children }: ChildrenProps) => (
  <span className={summaryCardClasses.bigNumber}>{children}</span>
);

const Caption = ({ children }: ChildrenProps) => (
  <span className={summaryCardClasses.caption}>{children}</span>
);
